import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { sessionsService } from '../../../services/sessions/sessions.service';

type DailyBriefUpdate = { dailyBrief: string | null; updatedByName: string };

const SELF_ECHO_TIMEOUT_MS = 10_000;

type UseDailyBriefControllerParams = {
  tenantId: string;
  employeeSession: string | null;
  isManager: boolean;
  onError: (message: string) => void;
};

// Summary: Daily-brief modal/red-dot state + manager save + SSE-echo toast (skips editor's tab).
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks
export function useDailyBriefController({
  tenantId,
  employeeSession,
  isManager,
  onError,
}: UseDailyBriefControllerParams) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // True between this tab issuing a save and its own SSE echo, so we skip self-notifying.
  const selfEditRef = useRef(false);
  const selfEditTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the self-echo flag + its safety timer (on echo consumed, save error, or unmount).
  const clearSelfEdit = useCallback(() => {
    selfEditRef.current = false;
    if (selfEditTimerRef.current) {
      clearTimeout(selfEditTimerRef.current);
      selfEditTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearSelfEdit(), [clearSelfEdit]);

  const open = useCallback(() => {
    setHasUnread(false);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const save = useCallback(
    async (text: string) => {
      if (!employeeSession || !isManager) {
        return;
      }
      setIsSaving(true);
      selfEditRef.current = true;
      // Safety: if the echo never arrives (disconnect/sleep), don't suppress later updates forever.
      if (selfEditTimerRef.current) {
        clearTimeout(selfEditTimerRef.current);
      }
      selfEditTimerRef.current = setTimeout(() => {
        selfEditRef.current = false;
        selfEditTimerRef.current = null;
      }, SELF_ECHO_TIMEOUT_MS);
      try {
        await sessionsService.updateDailyBrief(tenantId, text, employeeSession);
        setIsOpen(false);
      } catch (saveError: unknown) {
        clearSelfEdit();
        onError(saveError instanceof Error ? saveError.message : 'Failed updating daily brief');
      } finally {
        setIsSaving(false);
      }
    },
    [employeeSession, isManager, tenantId, onError, clearSelfEdit],
  );

  // SSE echo — toast + red dot for every tab except the editor's own (delta is patched upstream).
  const onUpdated = useCallback((event: DailyBriefUpdate) => {
    if (selfEditRef.current) {
      clearSelfEdit();
      return;
    }
    setHasUnread(true);
    const snippet =
      event.dailyBrief && event.dailyBrief.trim() !== ''
        ? event.dailyBrief.slice(0, 80)
        : 'הבריף היומי נוקה';
    notifications.show({
      color: 'blue',
      title: `${event.updatedByName} עדכן בריף יומי`,
      message: snippet,
      autoClose: 3000,
    });
  }, [clearSelfEdit]);

  return { isOpen, hasUnread, isSaving, open, close, save, onUpdated };
}
